import React, { useEffect, useRef, useState, type MutableRefObject } from 'react';
import Prism from 'prismjs';
import type { SupportedLanguage, SnippetTheme } from './snippet.types';
import { LANGUAGE_OPTIONS, THEME_OPTIONS } from './snippet.utils';
import '../../styles/features/snippet/SnippetPreview.css';

interface Props {
  code: string;
  language: SupportedLanguage;
  theme: SnippetTheme;
  title: string;
  previewRef: MutableRefObject<HTMLDivElement | null>;
}

// Explicit static import loaders — avoids webpack "Critical dependency" warning
// caused by dynamic template-literal imports.
const PRISM_LOADERS: Record<SupportedLanguage, () => Promise<void>> = {
  javascript: () => import('prismjs/components/prism-javascript' as string).then(() => {}),
  typescript: () => import('prismjs/components/prism-typescript' as string).then(() => {}),
  python: () => import('prismjs/components/prism-python' as string).then(() => {}),
  css: () => import('prismjs/components/prism-css' as string).then(() => {}),
  html: () => import('prismjs/components/prism-markup' as string).then(() => {}),
  json: () => import('prismjs/components/prism-json' as string).then(() => {}),
  bash: () => import('prismjs/components/prism-bash' as string).then(() => {}),
  go: () => import('prismjs/components/prism-go' as string).then(() => {}),
  rust: () => import('prismjs/components/prism-rust' as string).then(() => {}),
  java: () => import('prismjs/components/prism-java' as string).then(() => {}),
  csharp: () => import('prismjs/components/prism-csharp' as string).then(() => {}),
  markdown: () => import('prismjs/components/prism-markdown' as string).then(() => {}),
};

const SnippetPreview: React.FC<Props> = ({ code, language, theme, title, previewRef }) => {
  const [highlighted, setHighlighted] = useState('');
  const themeStyleRef = useRef<HTMLLinkElement | null>(null);

  // Load Prism CSS theme via <link> tag injection
  useEffect(() => {
    const themeOption = THEME_OPTIONS.find(t => t.value === theme);
    if (!themeOption) return;

    // Remove existing theme link
    if (themeStyleRef.current) {
      themeStyleRef.current.remove();
      themeStyleRef.current = null;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/${themeOption.cssFile}.min.css`;
    link.id = 'prism-theme-link';
    document.head.appendChild(link);
    themeStyleRef.current = link;

    return () => {
      link.remove();
      themeStyleRef.current = null;
    };
  }, [theme]);

  // Load Prism language component and highlight
  useEffect(() => {
    const langOption = LANGUAGE_OPTIONS.find(l => l.value === language);
    const prismId = langOption?.prismId || 'javascript';

    const doHighlight = () => {
      const grammar = Prism.languages[prismId];
      if (grammar) {
        const result = Prism.highlight(code, grammar, prismId);
        setHighlighted(result);
      } else {
        // Fallback: escape HTML and show plain
        const escaped = code
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        setHighlighted(escaped);
      }
    };

    // Try loading the language component via the static loader map
    if (!Prism.languages[prismId]) {
      const loader = PRISM_LOADERS[language];
      if (loader) {
        loader().then(doHighlight).catch(doHighlight);
      } else {
        doHighlight();
      }
    } else {
      doHighlight();
    }
  }, [code, language]);

  const langLabel = LANGUAGE_OPTIONS.find(l => l.value === language)?.label || language;
  const displayTitle = title || 'untitled';

  return (
    <div ref={previewRef} className="snippet-preview">
      {/* macOS-style window chrome */}
      <div className="snippet-preview__chrome">
        <div className="snippet-preview__dots">
          <span className="snippet-preview__dot snippet-preview__dot--red" />
          <span className="snippet-preview__dot snippet-preview__dot--yellow" />
          <span className="snippet-preview__dot snippet-preview__dot--green" />
        </div>
        <span className="snippet-preview__file-title">{displayTitle}</span>
        <span className="snippet-preview__lang-badge">{langLabel}</span>
      </div>

      {/* Highlighted code */}
      <div className="snippet-preview__code-wrap">
        <pre className={`language-${LANGUAGE_OPTIONS.find(l => l.value === language)?.prismId || language}`}>
          <code
            className={`language-${LANGUAGE_OPTIONS.find(l => l.value === language)?.prismId || language}`}
            dangerouslySetInnerHTML={{ __html: highlighted }}
          />
        </pre>
      </div>
    </div>
  );
};

export default SnippetPreview;
